import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { enGB, fiFI, nbNO, svSE } from "@clerk/localizations";
import { getBrowserLang, TranslocoService } from "@jsverse/transloco";
import * as Sentry from "@sentry/angular";

// PrimeNG locale imports
import { ClerkService, LocalizationResource } from "ngx-clerk-iliad";
import enGBFile from "primelocale/en-GB.json";
import { fi } from "primelocale/fi.json";
import nbNOFile from "primelocale/nb-NO.json";
import { sv } from "primelocale/sv.json";
import { PrimeNG } from "primeng/config";

// Application imports
import { BehaviorSubject, catchError, distinctUntilChanged, filter, first, map, of, switchMap } from "rxjs";
import { UserService } from "./user.service";
import { environment } from "../../environments/environment";
import { getCodeForLangKind, getKindForLang, isLanguageCode, LanguageCode } from "../domain/languages";
import { isClerkSessionEqual } from "../shared/types/clerk";

// PrimeNG locale types
const en = enGBFile["en-GB"];
const no = nbNOFile["nb-NO"];

type LocaleMessages = typeof en;
const LOCALES: Record<LanguageCode, LocaleMessages> = {
  fi,
  en,
  sv,
  no,
};

@Injectable({
  providedIn: "root",
})
export class LocalizationService {
  private static readonly LANG_KEY = "language";

  private _currentLanguage: LanguageCode;

  public get currentLanguage(): LanguageCode {
    return this._currentLanguage;
  }

  private _initialLanguageSet$ = new BehaviorSubject(false);
  public initialLanguageSet$ = this._initialLanguageSet$.asObservable();

  private availableLanguages: LanguageCode[];

  constructor(
    private readonly _translocoService: TranslocoService,
    private readonly _primengConfig: PrimeNG,
    private readonly _userService: UserService,
    private readonly _clerkService: ClerkService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.availableLanguages = this._translocoService.getAvailableLangs() as LanguageCode[];
    this._currentLanguage = "fi"; // Temporary default, will be overridden by the setter calls below

    // Use raw URL on initial load, as ActivatedRoute snapshot isn't available,
    // and subscribing to the query params observable always emits an empty value initially,
    // then emits again if there are any query parameters.
    const rawParams = new URLSearchParams(window.location.search);
    const langParam = rawParams.get("lang");

    // If we have a query parameter defined, it takes precedence.
    const langCode = this.getLangCode(langParam);
    if (langCode) {
      // Skip setting local storage--just because a user followed a link
      // it doesn't necessarily indicate they want to change their preference
      this.setCurrentLanguage(langCode, true);
      this._initialLanguageSet$.next(true);

      // Consume the query parameter so future navigation doesn't keep reapplying it.
      // Work directly with browser APIs, as Angular isn't yet aware of the URL or query params.
      rawParams.delete("lang");
      const newUrlString = `${window.location.pathname}${rawParams.toString() ? "?" + rawParams.toString() : ""}`;
      window.history.replaceState(null, "", newUrlString);
    } else {
      // If there's no query parameter, then read from local storage
      const storedLang = localStorage.getItem(LocalizationService.LANG_KEY);
      if (storedLang && isLanguageCode(storedLang) && this.availableLanguages.includes(storedLang)) {
        this.setCurrentLanguage(storedLang);
      } else {
        // If there's no local storage, use the browser language if possible
        const browserLanguage = this.normalizeLanguageCode(getBrowserLang());

        if (browserLanguage && this.availableLanguages.includes(browserLanguage)) {
          this.setCurrentLanguage(browserLanguage);
        } else {
          // If all of that fails, set to default language
          this.setCurrentLanguage(this._currentLanguage);
        }
      }
      this._initialLanguageSet$.next(true);
    }

    // When a Clerk session becomes available, sync the UI language with the user's stored preference.
    // If no preference is stored yet (e.g. first login), write the current UI language to the DB.
    this._clerkService.session$
      .pipe(
        filter((session) => session !== undefined && session !== null),
        // Clerk re-emits on token refresh / window refocus; only react when the session itself changes.
        distinctUntilChanged(isClerkSessionEqual),
        switchMap((session) => {
          // If metadata doesn't yet have a "cid"/"localcid", then initial sign-up hasn't completed, but we should
          // receive another session$ update momentarily.
          if (!session.user.publicMetadata[environment.clerk.cidClaimName]) {
            return of(null);
          }
          return this._userService.getSelf$(true).pipe(
            first(),
            catchError((e) => {
              Sentry.captureMessage("Failed to get user in LocalizationService during sign-in.", {
                level: "error",
                extra: e,
              });
              return of(null);
            }),
            switchMap((user) => {
              if (!user) {
                return of(null);
              }
              const dbLang = user.settings?.language;
              if (!dbLang) {
                // No language stored in DB yet — persist the current UI language
                return this._userService
                  .updateSettings({ language: getKindForLang(this._currentLanguage) }, { skipUserReload: true })
                  .pipe(map(() => null));
              }
              return of(dbLang);
            }),
          );
        }),
      )
      .subscribe((dbLang) => {
        if (!dbLang) {
          return;
        }
        const langCode = getCodeForLangKind(dbLang);
        if (langCode !== this._currentLanguage && this.availableLanguages.includes(langCode)) {
          this.changeLanguage(langCode, { skipRemoteUpdate: true, skipReload: true });
        }
      });
  }

  private normalizeLanguageCode(browserLangCode: string | undefined): LanguageCode | null {
    if (browserLangCode === undefined) {
      return null;
    }
    // no need to do any mapping if the browserLangCode is of type LanguageCode
    if (isLanguageCode(browserLangCode)) {
      return browserLangCode;
    }

    // Fill inn missing keys as needed to resolve different possible brower languagecodes
    const conversions: Record<string, LanguageCode> = {
      // Normalize other norwegian language codes
      "nn-NO": LanguageCode.no, // Nynorsk
      nn: LanguageCode.no, // Nynorsk
      "nb-NO": LanguageCode.no, // Bokmål
      nb: LanguageCode.no, // Bokmål

      "en-GB": LanguageCode.en,
      "en-US": LanguageCode.en,
    };

    return conversions[browserLangCode] ?? null;
  }

  private setCurrentLanguage(value: LanguageCode, skipStorage = false): void {
    this._currentLanguage = value;
    if (!skipStorage) {
      localStorage.setItem(LocalizationService.LANG_KEY, value);
    }
    this._translocoService.setActiveLang(value);
    this.applyPrimeNGLocale(value);

    this.document.documentElement.lang = value;
  }

  private getLangCode(langParam: string | null): LanguageCode | null {
    if (!isLanguageCode(langParam)) {
      return null;
    }

    if (!langParam || !this.availableLanguages.includes(langParam)) {
      return null;
    }

    return langParam;
  }

  /**
   * Changes the application's language, stores the user preference remotely,
   * then reloads the application.
   * @param lang The language code to change to.
   * @param options Allows callers to skip certain steps.
   *  * skipRemoteUpdate: If true, skips the HTTP call that updates user preferences remotely.
   *  * skipReload: If true, skips the hard refresh. Instead, the current language is changed in-place.
   *       Meant for use cases (such as sign-in) when we're certain we won't end up with stale localization data.
   */
  public changeLanguage(lang: LanguageCode, options?: { skipRemoteUpdate?: boolean; skipReload?: boolean }): void {
    localStorage.setItem(LocalizationService.LANG_KEY, lang);

    if (options?.skipRemoteUpdate) {
      if (options?.skipReload) {
        this.setCurrentLanguage(lang);
      } else {
        window.location.reload();
      }
      return;
    }

    this._userService
      .updateSettings(
        {
          language: getKindForLang(lang),
        },
        // If we're skipping a reload, we *don't* want to skip reloading the user.
        // If we *aren't* skipping a reload, then we *do* need to skip reloading the user,
        // as we're about to do a whole window reload, and it'd cancel the request midway anyway.
        { skipUserReload: !options?.skipReload },
      )
      .subscribe(() => {
        if (options?.skipReload) {
          this.setCurrentLanguage(lang);
        } else {
          window.location.reload();
        }
      });
  }

  private applyPrimeNGLocale(lang: LanguageCode): void {
    // Hard default to Finnish locale if the language is not supported (or imported) for PrimeNG
    const primeNgLocale = LOCALES[lang] ?? LOCALES["fi"];

    this._primengConfig.setTranslation(primeNgLocale);
  }

  /**
   * Returns a localization object to be passed to Clerk initialization
   */
  public getClerkLocalization(): LocalizationResource {
    const lang = this.currentLanguage;
    const v: Record<LanguageCode, LocalizationResource> = {
      en: enGB,
      sv: svSE,
      fi: fiFI,
      no: nbNO,
    };

    return v[lang] ?? fiFI;
  }
}
